// A single cancellable poller.
//
// `createPoller` runs `fn` on a fixed interval. `fn` returns a promise that
// resolves to `true` when polling is done. The poller guarantees:
//   - invocations never overlap: if a tick is still in flight when the next
//     interval fires, that tick is skipped;
//   - both the interval and the safety timeout are cleared on done, cancel,
//     and timeout;
//   - `onTimeout` fires at most once, and never after cancel/done.
//
// Timer functions are injectable so the poller can be unit-tested without a
// DOM or real timers.

export interface PollerOptions {
  intervalMs: number;
  timeoutMs: number;
  onTimeout?: () => void;
  // Injectable timers (default to globals). Used for tests.
  setInterval?: (handler: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
  setTimeout?: (handler: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

export interface Poller {
  cancel(): void;
}

export function createPoller(
  fn: () => Promise<boolean>,
  opts: PollerOptions,
): Poller {
  const setIntervalFn = opts.setInterval ?? ((h, ms) => setInterval(h, ms));
  const clearIntervalFn = opts.clearInterval ?? ((h) => clearInterval(h as any));
  const setTimeoutFn = opts.setTimeout ?? ((h, ms) => setTimeout(h, ms));
  const clearTimeoutFn = opts.clearTimeout ?? ((h) => clearTimeout(h as any));

  let intervalHandle: unknown = null;
  let timeoutHandle: unknown = null;
  let inFlight = false;
  let stopped = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (intervalHandle !== null) {
      clearIntervalFn(intervalHandle);
      intervalHandle = null;
    }
    if (timeoutHandle !== null) {
      clearTimeoutFn(timeoutHandle);
      timeoutHandle = null;
    }
  };

  const tick = () => {
    if (stopped || inFlight) return; // skip if a previous tick is still running
    inFlight = true;
    Promise.resolve()
      .then(fn)
      .then((done) => {
        if (done && !stopped) stop();
      })
      .catch(() => {
        // swallow errors; keep polling until done/timeout/cancel
      })
      .finally(() => {
        inFlight = false;
      });
  };

  intervalHandle = setIntervalFn(tick, opts.intervalMs);
  timeoutHandle = setTimeoutFn(() => {
    if (stopped) return;
    timeoutHandle = null; // already fired; nothing left to clear for it
    stop();
    opts.onTimeout?.();
  }, opts.timeoutMs);

  return {
    cancel: stop,
  };
}
